import { fileTypeFromBlob, fileTypeFromBuffer } from 'file-type';

import { getImageTypeByUrl } from '../photos';
import request from './request';

import { ENDPOINT, HEADERS } from '../../constants/api';
import { IMAGE_TYPE } from '../../constants/photos';

const studio = {
  createFolder({ db, name, shopId }) {
    return request.post({
      endpoint: ENDPOINT.WEB_V2,
      params: { db },
      payload: { folderName: name },
      url: `/shops/${shopId}/mediaLibrary/folders/`,
    });
  },
  deleteFolder({ db, folder, shopId }) {
    return request.delete({
      endpoint: ENDPOINT.WEB_V2,
      params: { db },
      url: `/shops/${shopId}/mediaLibrary/folders/${folder}`,
    });
  },
  deleteImages({ db, shopId, uuids }) {
    return request.delete({
      endpoint: ENDPOINT.WEB_V2,
      params: { db },
      payload: { uuids },
      url: `/shops/${shopId}/mediaLibrary/files`,
    });
  },
  downloadImage({ data, signal, url }) {
    return new Promise(function executor(resolve, reject) {
      if (data instanceof ArrayBuffer) {
        fileTypeFromBuffer(data)
          .then(
            function onFileType({ ext, mime }) {
              if (signal && signal.aborted) return;

              resolve({ blob: new Blob([data]), format: ext, mime });
            },
            reject,
          );
      } else {
        const type = getImageTypeByUrl(url);

        if (type === null) reject('Unknown image type');

        const options = {
          credentials: type === IMAGE_TYPE.ETSY,
          signal,
          url,
        };

        if (options.credentials) {
          options.params = { url };
          options.url = '/getImage64';
        }

        request
          .get(options)
          .then(
            function onImageDownloaded(blob) {
              if (signal && signal.aborted) return;

              fileTypeFromBlob(blob)
                .then(
                  function onFileType({ ext, mime }) {
                    resolve({ blob, format: ext, mime });
                  },
                  reject,
                );
            },
            reject,
          );
      }
    });
  },
  downloadImages({ db, shopId, uuids }) {
    if (typeof uuids === 'string') {
      return request.get({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        url: `/shops/${shopId}/mediaLibrary/download/${uuids}`,
      });
    } else if (Array.isArray(uuids)) {
      return request.post({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        payload: { uuids: uuids },
        url: `/shops/${shopId}/mediaLibrary/bulkDownload`,
      });
    } else {
      return Promise.reject();
    }
  },
  getFolders({ db, shopId, signal }) {
    return request.get({
      endpoint: ENDPOINT.WEB_V2,
      params: { db },
      signal,
      url: `/shops/${shopId}/mediaLibrary/folders`,
    });
  },
  getImages({ db, filters, folder, shopId, signal }) {
    const params = { ...filters, db };

    return request.get({
      endpoint: ENDPOINT.WEB_V2,
      params,
      signal,
      url: folder
        ? `/shops/${shopId}/mediaLibrary/folders/${folder}/images`
        : `/shops/${shopId}/mediaLibrary/images`,
    });
  },
  getListings({ db, groupId, limit, offset, shopId, signal }) {
    return request.get({
      endpoint: ENDPOINT.WEB_V2,
      params: { db, limit, offset },
      signal,
      url: `/shops/${shopId}/mediaLibrary/images/group/${groupId}`,
    });
  },
  moveImages({ db, shopId, source: sourceFolderUuid, target: destinationFolderUuid, uuids }) {
    return request.put({
      endpoint: ENDPOINT.WEB_V2,
      params: { db },
      payload: {
        uuids,
        destinationFolderUuid,
        sourceFolderUuid,
      },
      url: `/shops/${shopId}/mediaLibrary/files/move`,
    });
  },
  renameFolder({ db, folder, name, shopId }) {
    return request.put({
      endpoint: ENDPOINT.WEB_V2,
      params: { db },
      payload: { name },
      url: `/shops/${shopId}/mediaLibrary/folders/${folder}`,
    });
  },
  savePhoto({ altText, background, db, folder, format, layer, name, shopId, signal, url }) {
    return request.post({
      endpoint: ENDPOINT.WEB_V2,
      params: { db },
      payload: {
        alt_text: altText,
        fileName: name,
        folderUuid: folder || undefined,
        format,
        is_layer: !!layer,
        is_studio_background: !!background,
        url,
      },
      signal,
      url: `/shops/${shopId}/mediaLibrary/files`,
    });
  },
  updatePhoto({ altText, db, folder, format, name, shopId, url, uuid }) {
    return request.put({
      endpoint: ENDPOINT.WEB_V2,
      params: { db },
      payload: { alt_text: altText, fileName: name, folderUuid: folder || null, format, url },
      url: `/shops/${shopId}/mediaLibrary/files/${uuid}`,
    });
  },
  updatePhotoAltText({ altText, db, shopId, uuid }) {
    return request.put({
      endpoint: ENDPOINT.WEB_V2,
      params: { db },
      payload: { alt_text: altText },
      url: `/shops/${shopId}/mediaLibrary/files/${uuid}`,
    });
  },
  uploadToS3({ data, db, mime, shopId }) {
    return request.get({
      endpoint: ENDPOINT.WEB_V2,
      params: { db, mime },
      url: `/shops/${shopId}/mediaLibrary/files/uploadUrl`,
    })
      .then(function onUploadURL({ uploadUrl }) {
        return request.put({
          headers: { [HEADERS.CONTENT_TYPE]: mime, [HEADERS.EXPIRES]: 0 },
          payload: data,
          url: uploadUrl,
        })
          .then(function onUploaded() {
            const url = new URL(uploadUrl);
            return Promise.resolve({ url: `${url.protocol}/${url.pathname}` });
          });
      });
  },

  // TODO: delete with FEATURE_FLAG_STUDIO_V2
  getScene({ scene, signal }) {
    return request.get({
      endpoint: ENDPOINT.WEB_V2,
      params: { scene },
      signal,
      url: '/photoEditor/scenery',
    });
  },
  getUnsplashDownloadURL(downloadLocationUrl) {
    return request.post({
      payload: { downloadLocationUrl },
      url: '/shops/unsplash/download',
    });
  },
  isolate({ image, shopId }) {
    const payload = new FormData();
    payload.append('image_file', image);

    return request.post({
      endpoint: ENDPOINT.WEB_V2,
      payload,
      url: `/aiPhotoEditor/shops/${shopId}/removeBackground`,
    });
  },
  searchUnsplash(params) {
    return request.get({
      params,
      url: '/shops/unsplash/search',
    });
  },
  smartRemove({ image, mask }) {
    const payload = new FormData();
    payload.append('image_file', image);
    payload.append('mask_file', mask);

    return request.post({
      endpoint: ENDPOINT.WEB_V2,
      payload,
      url: '/photoEditor/smart-remove',
    });
  },
};

export default studio;
